import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AppContext = createContext(null);

// Cache version - bump this to force all clients to re-fetch
const CACHE_VERSION = 'v6_not_selected';
const CACHE_KEY = 'camp_cache_version';

// Auto-clear stale localStorage on version change
if (localStorage.getItem(CACHE_KEY) !== CACHE_VERSION) {
  // Clear attendance data that had old 'present' defaults
  localStorage.removeItem('camp_attendance');
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('camp_') && key !== 'camp_auth' && key !== 'camp_notes') {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(k => localStorage.removeItem(k));
  localStorage.setItem(CACHE_KEY, CACHE_VERSION);
}

const CAMP_DATES = [
  { date: '2026-04-06', label: 'Mon 4/6', fullDate: 'Monday, April 6, 2026' },
  { date: '2026-04-07', label: 'Tue 4/7', fullDate: 'Tuesday, April 7, 2026' },
  { date: '2026-04-08', label: 'Wed 4/8', fullDate: 'Wednesday, April 8, 2026' },
  { date: '2026-04-09', label: 'Thu 4/9', fullDate: 'Thursday, April 9, 2026' },
  { date: '2026-04-10', label: 'Fri 4/10', fullDate: 'Friday, April 10, 2026' },
];

const GROUP_LETTER_TO_NAME = {
  'A': 'Group A - Nursery',
  'B': 'Group B - PreK',
  'C': 'Group C - Kindergarten',
  'D': 'Group D - 1st Grade',
  'E': 'Group E - 2nd/3rd Grade',
  'F': 'Group F - 3rd-5th Grade',
};

const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' && !window.location.hostname.includes('preview.emergentagent.com') ? `${window.location.origin}/api` : `${process.env.REACT_APP_BACKEND_URL}/api`;
const safeFetch = (...args) => (window.__origFetch || window.fetch)(...args);

export function AppProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('camp_auth') === 'true';
  });

  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    const match = CAMP_DATES.find(d => d.date === today);
    if (match) return match.date;
    // Pick nearest upcoming camp day
    const upcoming = CAMP_DATES.find(d => d.date >= today);
    return upcoming ? upcoming.date : CAMP_DATES[CAMP_DATES.length - 1].date;
  });

  const [campers, setCampers] = useState(() => {
    const saved = localStorage.getItem('camp_campers');
    return saved ? JSON.parse(saved) : [];
  });

  const [staff, setStaff] = useState(() => {
    const saved = localStorage.getItem('camp_staff_v2');
    return saved ? JSON.parse(saved) : [];
  });

  // Expected per-group per-day counts from main sheet (source of truth for display)
  const [expectedCounts, setExpectedCounts] = useState(() => {
    const saved = localStorage.getItem('camp_expected_counts');
    return saved ? JSON.parse(saved) : {};
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(() => {
    return localStorage.getItem('camp_last_fetch') || null;
  });

  const [attendance, setAttendance] = useState(() => {
    const saved = localStorage.getItem('camp_attendance');
    return saved ? JSON.parse(saved) : {};
  });

  const [camperNotes, setCamperNotes] = useState(() => {
    const saved = localStorage.getItem('camp_notes');
    return saved ? JSON.parse(saved) : {};
  });

  const [masterRoster, setMasterRoster] = useState(() => {
    const saved = localStorage.getItem('camp_master_roster');
    return saved ? JSON.parse(saved) : [];
  });

  const [counselorAbsences, setCounselorAbsences] = useState(() => {
    const saved = localStorage.getItem('camp_counselor_absences');
    return saved ? JSON.parse(saved) : {};
  });

  const [counselorAdditions, setCounselorAdditions] = useState(() => {
    const saved = localStorage.getItem('camp_counselor_additions');
    return saved ? JSON.parse(saved) : {};
  });

  // Persist
  useEffect(() => { localStorage.setItem('camp_auth', isAuthenticated.toString()); }, [isAuthenticated]);
  useEffect(() => { localStorage.setItem('camp_attendance', JSON.stringify(attendance)); }, [attendance]);
  useEffect(() => { localStorage.setItem('camp_notes', JSON.stringify(camperNotes)); }, [camperNotes]);
  useEffect(() => { localStorage.setItem('camp_master_roster', JSON.stringify(masterRoster)); }, [masterRoster]);
  useEffect(() => { localStorage.setItem('camp_counselor_absences', JSON.stringify(counselorAbsences)); }, [counselorAbsences]);
  useEffect(() => { localStorage.setItem('camp_counselor_additions', JSON.stringify(counselorAdditions)); }, [counselorAdditions]);
  useEffect(() => {
    if (campers.length > 0) localStorage.setItem('camp_campers', JSON.stringify(campers));
  }, [campers]);
  useEffect(() => {
    if (staff.length > 0) localStorage.setItem('camp_staff_v2', JSON.stringify(staff));
  }, [staff]);
  useEffect(() => {
    if (Object.keys(expectedCounts).length > 0) localStorage.setItem('camp_expected_counts', JSON.stringify(expectedCounts));
  }, [expectedCounts]);

  const login = (password) => {
    if (password === 'Camp1993') {
      setIsAuthenticated(true);
      return true;
    }
    return false;
  };
  const logout = () => setIsAuthenticated(false);

  const fetchSheetData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [campersRes, staffRes] = await Promise.all([
        safeFetch(`${API}/sheets/campers`),
        safeFetch(`${API}/sheets/staff`)
      ]);
      const campersData = await campersRes.json();
      const staffData = await staffRes.json();

      if (!campersData.success) {
        setError(campersData.error || 'Failed to load camper data');
        setLoading(false);
        return;
      }

      setCampers(campersData.data.campers);

      // Store the expected counts from the main sheet
      if (campersData.data.expectedCounts) {
        setExpectedCounts(campersData.data.expectedCounts);
      }

      if (staffData.success && staffData.data.staff.length > 0) {
        setStaff(staffData.data.staff);
        if (masterRoster.length === 0) {
          const rosterFromSheet = staffData.data.staff.map((s, idx) => ({
            id: s.id || `sheet_staff_${idx}`,
            name: s.name,
            role: s.role,
            group: s.groupName || s.group
          }));
          setMasterRoster(rosterFromSheet);
        }
      }

      const now = new Date().toISOString();
      setLastFetchTime(now);
      localStorage.setItem('camp_last_fetch', now);
    } catch (err) {
      setError('Failed to fetch data: ' + err.message);
    }
    setLoading(false);
  }, [masterRoster.length]);

  useEffect(() => {
    if (isAuthenticated && campers.length === 0) {
      fetchSheetData();
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get campers for a date (only those with "1" flags)
  const getCampersForDate = useCallback((date) => {
    return campers.filter(c => c.daysAttending && c.daysAttending.includes(date));
  }, [campers]);

  // Get groups for a date — uses dayGroups column mapping for accurate assignment
  // Handles double-marked campers (marks in 2 group columns) by showing in both
  const getGroupsForDate = useCallback((date) => {
    const dateCampers = getCampersForDate(date);
    const groups = {};
    const groupNameMap = {
      'A': 'Group A - Nursery',
      'B': 'Group B - PreK',
      'C': 'Group C - Kindergarten',
      'D': 'Group D - 1st Grade',
      'E': 'Group E - 2nd/3rd Grade',
      'F': 'Group F - 3rd-5th Grade',
    };
    dateCampers.forEach(camper => {
      // Primary group from dayGroups
      const dayGroupLetter = camper.dayGroups?.[date] || camper.groupLetter || '';
      const group = groupNameMap[dayGroupLetter] || camper.group || 'Unassigned';
      if (!groups[group]) groups[group] = [];
      groups[group].push(camper);

      // Also add to extra groups if double-marked in the sheet
      const extras = camper.dayExtraGroups?.[date] || [];
      extras.forEach(extraLetter => {
        const extraGroup = groupNameMap[extraLetter];
        if (extraGroup) {
          if (!groups[extraGroup]) groups[extraGroup] = [];
          groups[extraGroup].push(camper);
        }
      });
    });
    return groups;
  }, [getCampersForDate]);

  // Get the EXPECTED count for a date (from main sheet Total row)
  const getExpectedCountForDate = useCallback((date) => {
    const dayCounts = expectedCounts[date];
    if (!dayCounts) return getCampersForDate(date).length;
    // Use the 'total' field from main sheet Row 5 (includes half-day)
    if (dayCounts.total !== undefined) return dayCounts.total;
    return Object.values(dayCounts).reduce((sum, c) => sum + (typeof c === 'number' ? c : 0), 0);
  }, [expectedCounts, getCampersForDate]);

  // Get the EXPECTED count for a specific group on a date
  const getExpectedGroupCount = useCallback((date, groupLetter) => {
    const dayCounts = expectedCounts[date];
    if (!dayCounts) return null;
    return dayCounts[groupLetter] || null;
  }, [expectedCounts]);

  // Attendance helpers
  const getAttendance = useCallback((camperId, date) => {
    const key = `${camperId}_${date}`;
    return attendance[key] || { status: 'not_selected', lateTime: null, earlyTime: null };
  }, [attendance]);

  const setAttendanceForCamper = useCallback((camperId, date, data) => {
    const key = `${camperId}_${date}`;
    setAttendance(prev => {
      const existing = prev[key] || { status: 'not_selected', lateTime: null, earlyTime: null };
      return {
        ...prev,
        [key]: { ...existing, ...data }
      };
    });
  }, []);

  const getNotes = useCallback((camperId, date) => {
    return camperNotes[`${camperId}_${date}`] || '';
  }, [camperNotes]);

  const setNotesForCamper = useCallback((camperId, date, notes) => {
    setCamperNotes(prev => ({ ...prev, [`${camperId}_${date}`]: notes }));
  }, []);

  // Counselor management
  const addCounselor = useCallback((counselor) => {
    setMasterRoster(prev => [...prev, { ...counselor, id: Date.now().toString() }]);
  }, []);
  const removeCounselor = useCallback((counselorId) => {
    setMasterRoster(prev => prev.filter(c => c.id !== counselorId));
  }, []);
  const updateCounselor = useCallback((counselorId, data) => {
    setMasterRoster(prev => prev.map(c => c.id === counselorId ? { ...c, ...data } : c));
  }, []);
  const markCounselorAbsent = useCallback((counselorId, date, isAbsent) => {
    setCounselorAbsences(prev => {
      const dateAbsences = prev[date] || [];
      if (isAbsent) return { ...prev, [date]: [...new Set([...dateAbsences, counselorId])] };
      return { ...prev, [date]: dateAbsences.filter(id => id !== counselorId) };
    });
  }, []);
  const addDayCounselor = useCallback((date, counselor) => {
    setCounselorAdditions(prev => {
      const dateAdditions = prev[date] || [];
      return { ...prev, [date]: [...dateAdditions, { ...counselor, id: Date.now().toString() }] };
    });
  }, []);
  const removeDayCounselor = useCallback((date, counselorId) => {
    setCounselorAdditions(prev => {
      const dateAdditions = prev[date] || [];
      return { ...prev, [date]: dateAdditions.filter(c => c.id !== counselorId) };
    });
  }, []);
  const getCounselorsForDate = useCallback((date) => {
    const absences = counselorAbsences[date] || [];
    const additions = counselorAdditions[date] || [];
    return {
      active: masterRoster.filter(c => !absences.includes(c.id)).map(c => ({ ...c, isAbsent: false, isTemporary: false })),
      absent: masterRoster.filter(c => absences.includes(c.id)).map(c => ({ ...c, isAbsent: true, isTemporary: false })),
      temporary: additions.map(c => ({ ...c, isAbsent: false, isTemporary: true }))
    };
  }, [masterRoster, counselorAbsences, counselorAdditions]);

  const value = {
    isAuthenticated, login, logout,
    campers, staff, loading, error, lastFetchTime,
    fetchSheetData,
    selectedDate, setSelectedDate, campDates: CAMP_DATES,
    getCampersForDate, getGroupsForDate,
    getExpectedCountForDate, getExpectedGroupCount, expectedCounts,
    getAttendance, setAttendanceForCamper, attendance,
    getNotes, setNotesForCamper,
    masterRoster, addCounselor, removeCounselor, updateCounselor,
    markCounselorAbsent, addDayCounselor, removeDayCounselor, getCounselorsForDate,
    counselorAbsences, counselorAdditions,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
}

export { CAMP_DATES };
